/* eslint-disable @typescript-eslint/no-explicit-any */
import {HttpError} from './types';

/**
 * Parses a Fetch API Response based on its content-type header.
 *
 * This function is the single place where HTTP response bodies are interpreted.
 * When Zod is introduced, pass a ZodSchema<T> as the `schema` parameter and
 * call schema.parse(body) before returning — no other changes will be needed.
 *
 * @param response - The resolved Fetch API Response object.
 * @param schema - Reserved for future Zod schema validation. Pass undefined for now.
 * @returns A promise resolving to the parsed response body as type T.
 * @throws {HttpError} when the server returns a non-2xx status. The `status` property
 *   contains the numeric HTTP status code for semantic error handling by callers.
 */
async function parseResponse<T = any>(response: Response, schema?: unknown): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';

  if (!response.ok) {
    let errorBody: string;
    try {
      errorBody = contentType.includes('application/json')
        ? JSON.stringify(await response.json())
        : await response.text();
    } catch {
      errorBody = '(could not read response body)';
    }
    throw new HttpError(response.status, errorBody);
  }

  let body: any;
  if (contentType.includes('officedocument.spreadsheet')) {
    body = await response.arrayBuffer();
  } else if (contentType.includes('application/json')) {
    body = await response.json();
  } else {
    body = await response.text();
  }

  // Zod seam: when schema is provided, validate here before returning.
  // Replace the block below with: return (schema as ZodSchema<T>).parse(body);
  if (schema !== undefined) {
    // Future: return (schema as ZodSchema<T>).parse(body);
    console.warn('parseResponse: schema validation is not yet implemented. Schema was passed but will be ignored.');
  }

  return body as T;
}

export {parseResponse};
